import React from "react";
import { Autocomplete, TextField, Box, Chip } from "@mui/material";

/**
 * Reusable organization selector with search/autocomplete
 *
 * @param {Object} userData - User data containing orgs array
 * @param {string} selectedOrg - Currently selected org ID
 * @param {function} onOrgChange - Callback when selection changes
 * @param {boolean} disabled - Whether selector is disabled
 * @param {string[]} excludeOrgs - Array of org IDs to exclude from dropdown
 * @param {Set<string>} [trialOrgIds] - Org IDs known to be trial orgs (renders a "Trial" chip).
 *   The user-doc orgs array only carries orgID/orgName, so a row is a trial when either its own
 *   `trial` flag is set (forward-compatible if the backend adds it) or its id is in this set.
 */
export const OrgSelector = ({
  userData,
  selectedOrg,
  onOrgChange,
  disabled = false,
  excludeOrgs = [],
  trialOrgIds,
}) => {
  const isTrialOrg = (option) =>
    option?.trial === true || (trialOrgIds && trialOrgIds.has(option?.orgID));
  // Filter out excluded orgs and sort alphabetically
  const availableOrgs = (userData?.orgs?.filter(
    (org) => !excludeOrgs.includes(org.orgID)
  ) || []).sort((a, b) => (a.orgName || "").localeCompare(b.orgName || ""));

  const selectedOption = availableOrgs.find((org) => org.orgID === selectedOrg) || null;

  return (
    <Autocomplete
      id="org-select"
      options={availableOrgs}
      getOptionLabel={(option) => option.orgName || ""}
      value={selectedOption}
      onChange={(_, newValue) => {
        if (newValue) onOrgChange(newValue.orgID);
      }}
      disabled={disabled}
      disableClearable
      sx={{ minWidth: 280, mb: 2.5 }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.orgID} sx={{ display: "flex", gap: 1 }}>
          <span>{option.orgName || ""}</span>
          {isTrialOrg(option) && (
            <Chip
              label="Trial"
              size="small"
              color="info"
              sx={{ ml: "auto", height: 20 }}
              data-testid="org-trial-chip"
            />
          )}
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} placeholder="Search organizations..." variant="outlined" />
      )}
    />
  );
};

export default OrgSelector;
